import React, { useState } from 'react';
import styles from '../modals/css/GenericModal.module.css';
import { Button } from '@/components/Buttons';
import { useResetUserPassword } from '@/hooks/user/useResetUserPassword';
import { useSelector, useDispatch } from "react-redux";
import { setToast } from "@/store/systemSlice";
import { useTranslation } from 'react-i18next';

interface PasswordResetModalProps {
  isOpen: boolean;
  userId: string;
  userName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  userId,
  userName,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('UsersManagement');
  const [newPassword, setNewPassword] = useState('');
  const dispatch = useDispatch();

  const resetPasswordMutation = useResetUserPassword({
    onSuccess: (data) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
      onSuccess();
      onClose();
      setNewPassword('');
    },
    onError: (error: any) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה באיפוס הסיסמה",
          toastType: "error",
        })
      );
    },
  });

  const handleReset = () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: t("passwordTooShort"),
          toastType: "error",
        })
      );
      return;
    }

    resetPasswordMutation.mutate({
      user_id: userId,
      new_password: newPassword,
    });
  };

  const handleClose = () => {
    if (!resetPasswordMutation.isPending) {
      setNewPassword('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <h2 className={styles.h2}>{t("resetPasswordTitle")}</h2>
        <p className={styles.p}>
          {t("resetPasswordDescription", { userName })}
        </p>
        
        <div className="mb-4">
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {t("newPassword")}
          </label>
          <input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("enterNewPassword")}
            disabled={resetPasswordMutation.isPending}
            minLength={6}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("passwordMinLength")}
          </p>
        </div>

        <div className={styles.buttonContainer}>
          <Button 
            data-testid="password-reset-cancel-btn" 
            icon='no' 
            id='cancel' 
            popupstyle="gray" 
            onClick={handleClose} 
            text={t("cancel")}
            disabled={resetPasswordMutation.isPending}
          />
          <Button 
            data-testid="password-reset-confirm-btn" 
            icon='no' 
            id='confirm' 
            popupstyle="red" 
            onClick={handleReset} 
            loading={resetPasswordMutation.isPending} 
            disabled={resetPasswordMutation.isPending || !newPassword.trim()} 
            text={t("resetPassword")}
          />
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;